import type Page from "./Page";

import BaseObject, { globalOnChange } from "./BaseObject";
import type BaseModel from "./BaseModel";
import WatchableValue from "./WatchableValue";
import Logger from "../Logger";
import PaneObject from "./PaneObject";
import TabItemData from "./TabItemData";
import { itx } from "../translationItx/itx";
import type Floor from "./Floor";
import { showToast } from "../components/Toasts";
import { pageTypeInfo } from "./PageTypes";
import { EventKey } from "../eventLogger/EventKeys";

const logger = new Logger();

export class WorkingPane {
  item: BaseObject | undefined = undefined;

  isTransitOpen: boolean = false;
  closeable = true;
  screen2: boolean = false;
  activePanes: ActivePanes;
  tabItemData: TabItemData | undefined;
  subPageId: string | undefined;
  title?: string;
  // last page scrolled to in a PDF viewer, so switching tabs and back keeps the position
  lastViewedPage?: number;

  constructor(item: BaseObject, activePanes: ActivePanes, screen2?: boolean) {
    this.activePanes = activePanes;
    this.item = item;
    this.screen2 = screen2 || false;

    if (item.className == "PaneObject") {
      this.closeable = (item as PaneObject).closeable;
    }
  }

  get id(): string {
    if (this.item) {
      return this.item.id;
    } else {
      return "";
    }
  }
  get tag(): string {
    if (this.item) {
      // const parent = this.item.parent;
      // if (parent) {
      //   if (this.item.tagOrName == "") {
      //     if (parent.parent) {
      //       return `${parent.parent.tagOrName}/${parent.tag}`;
      //     } else {
      //       return parent.tagOrName;
      //     }
      //   } else {
      //     return `${parent.tagOrName}/${this.item.tag}`;
      //   }
      // }
      return `${this.item.fullTag}`;
    }
    return "???";
  }

  set tag(newValue: string) {
    if (this.item) {
      this.item.tag = newValue;
    }
  }

  get name(): string {
    if (this.item) {
      const parent = this.item.parent;
      if (parent) {
        if (this.item.tagOrName == "") {
          if (parent.parent) {
            return parent.name;
          }
        } else {
          return this.item.name;
        }
      }
      return this.item.name;
    }
    return "???";
  }

  get active(): boolean {
    return this.activePanes.getActiveId() == this.id;
  }

  set active(newValue: boolean) {
    if (newValue) {
      this.activePanes.setActiveId(this.id);
    }
  }
}

export default class ActivePanes extends BaseObject {
  public get className(): string {
    return "ActivePanes"; // no itx
  }

  get isPersistent(): boolean {
    return false;
  }

  noPaneActiveBecauseOnlyNavigationIsVisible = false;

  items: Array<WorkingPane> = new Array<WorkingPane>();

  screen2 = false;

  activeItemIndex = new WatchableValue(-1);

  // this object will we used and referenced by its id "help" in menubar.tsc: 830
  helpPaneObject: PaneObject | undefined;
  aiPaneObject: PaneObject | undefined;
  etsPostImportObject: PaneObject | undefined;
  pdfListPaneObject: PaneObject | undefined;
  /////////////////////////////////////////////////////////////////////////////////

  private globalOnChangeSubscription: string | undefined;

  initFixedPanes() {
    if (this.screen2) {
      return;
    }

    const homePageItem = new PaneObject("home", "Estada", false); // no itx
    const homePagePane = new WorkingPane(homePageItem, this);

    const releaseNotesItem = new PaneObject("releaseNotes", "Release Notes", true); // no itx
    const releaseNotesPane = new WorkingPane(homePageItem, this);

    const commanderItem = new PaneObject("commander", "Commander", true); // no itx
    const commanderPane = new WorkingPane(commanderItem, this);

    this.helpPaneObject = new PaneObject(
      "help", // no itx
      itx("help", "Hilfe"),
      true,
    );

    this.aiPaneObject = new PaneObject("ai", "Assistent", true); // no itx
    this.etsPostImportObject = new PaneObject("etsPostImport", "Nachbearbeitung Ets-Import ", true); // no itx

    this.pdfListPaneObject = new PaneObject("pdfList", "PDF Liste", true); // no itx

    this.items.push(homePagePane);
  }

  constructor(screen2: boolean) {
    super();

    this.screen2 = screen2;

    this.initFixedPanes();

    this.globalOnChangeSubscription = globalOnChange.subscribe((obj: BaseObject, property: string | undefined) => {
      try {
        if (obj.isDeleted) {
          for (let i = 0; i < this.items.length; i++) {
            const pane = this.items[i];
            if (pane.item == obj) {
              this.closePane(pane.id);
            }
          }
        }
      } catch (ex) {}
    });
  }

  dispose() {
    if (this.globalOnChangeSubscription) {
      globalOnChange.unsubscribe(this.globalOnChangeSubscription);
      this.globalOnChangeSubscription = undefined;
    }
    this.items = new Array<WorkingPane>();
    this.activeItemIndex.value = -1;
  }

  idToIndex(id: string): number {
    return this.items.findIndex((item) => item.id === id);
  }

  getActiveItem(): WorkingPane {
    return this.items[this.activeItemIndex.value];
  }

  getActiveId(): string {
    return this.items[this.activeItemIndex.value]?.id || "";
  }

  getActiveSubPageId(): string | undefined {
    return this.items[this.activeItemIndex.value]?.subPageId;
  }

  getPane(id: string) {
    return this.items.find((item) => item.id === id);
  }

  updateTabs() {
    for (let i = 0; i < this.items.length; i++) {
      const pane = this.items[i];
      pane.tabItemData?.raiseOnChange("update", undefined, true);
    }
  }

  setActiveId(id: string, subPageId?: string): void {
    var found = false;

    const lastActiveDocumentScreen = state.activeDocumentScreen;

    state.activeDocumentScreen = this.screen2 ? 2 : 1;
    const lastActiveId = this.getActiveId();
    const lastActiveSubPageId = this.getActiveSubPageId();
    const lastActiveItem = this.getActiveItem();

    if (lastActiveId == id && lastActiveSubPageId == subPageId) {
      if (lastActiveDocumentScreen != state.activeDocumentScreen) {
        lastActiveItem?.tabItemData?.raiseOnChange("active");
      }
      return;
    }

    for (let i = 0; i < this.items.length; i++) {
      const pane = this.items[i];
      let htmlId = `pane_${pane.id}`;
      if (pane.subPageId) {
        htmlId += `_${pane.subPageId}`;
      }

      const paneElm = document.getElementById(htmlId);

      if (pane.id === id && pane.subPageId == subPageId) {
        found = true;
        this.activeItemIndex.value = i;
        if (paneElm) {
          paneElm.style.visibility = "visible";
          //
          // Move this pane to the end of the parent, so that it is on top of all other panes
          // even if all othe panes have visibility hidden
          // backgrounds of svg Texts will we rendererd on top of other panes otherwise
          //
          paneElm.parentNode?.append(paneElm);

          const page = this.items[this.activeItemIndex.value]?.item;
          if (page?.className == "Page") {
            state.updateAllConnectionsWithoutUpdate();
          }
        }

        //
        // MWE 20.11.2024 Chat Fenster ist schon länger nicht mehgr aktiv
        //

        // {
        //   const chat = document.getElementById("lo-messenger-frame");
        //   if (chat) {
        //     if (state.activePaneId == "home") {
        //       chat.style.display = "block";
        //     } else {
        //       chat.style.display = "none";
        //     }
        //   }
        // }
      } else {
        if (paneElm) {
          if (paneElm.style.visibility != "hidden") {
            paneElm.style.visibility = "hidden";
          }
        }
      }
    }

    state.command("deactivePane", lastActiveId);
    if (found) {
      state.command("activePane", id);

      // redraw Connections
      const openPage = this.state.objectRegistry.getObject(id) as Page;
      if (openPage?.isRoomOrFloorPlan) {
        openPage.updateConnections();
      }
    } else {
      this.activeItemIndex.value = -1;
    }

    this.checkFloorplan(this.getActiveItem());

    this.getActiveItem()?.tabItemData?.raiseOnChange("active");
    lastActiveItem?.tabItemData?.raiseOnChange("active");

    this.raiseOnChange("index");
  }

  isOpen(id: string): boolean {
    for (let i = 0; i < this.items.length; i++)
      if (this.items[i].id === id) {
        return true;
      }

    return false;
  }

  loadPage(id: string, subPageId?: string): WorkingPane | undefined {
    var result: Page | undefined = this.state.objectRegistry.getObject(id);

    let pane: WorkingPane | undefined;

    if (result) {
      pane = this.openPane(result, subPageId);
      this.setActiveId(id, subPageId);
    } else {
      logger.error(`page ${id} not found`);
    }

    return pane;
  }

  openPane(item: BaseModel, subPageId?: string): WorkingPane | undefined {
    console.log("openPane", item);

    const existingPane = this.items.find((pane) => pane.id === item.id && pane.subPageId === subPageId);
    if (existingPane) {
      this.activatePane(item.id, subPageId);
      return existingPane;
    }

    const pane = new WorkingPane(item, this, this.screen2);
    pane.subPageId = subPageId;

    this.items.push(pane);
    state.eventLogger.addEvent(EventKey.ObjectOpened, {
      objectType: item.className,
      obj: item.id + "_" + item.tag + "_" + item.name,
    });
    this.activatePane(item.id, subPageId);
    let selected: BaseModel | undefined;
    if (item.className == "Page") {
      selected = (item as Page).getPageSelectionElement();
    }
    if (!selected) {
      state.selectionManager.selectedObject = item;
    } else {
      state.selectionManager.selectedObject = selected;
    }

    this.raiseOnChange("open");
    return pane;
  }

  checkFloorplan(item: WorkingPane) {
    if (item.item?.className == "Page" && (item.item as Page).isFloorPlan && (item.item as Page).backgroundImgUrl) {
      const floor = (item.item as Page).parent as Floor;
      const cnt = floor.hasRoomsWithoutPlacementInFloorPlanAndNoBackgroundImg;

      (item.item as Page).editRooms = floor.rooms.size - cnt == 0;

      if (cnt) {
        let s = itx("ActivePanes.RoomsWithoutPlacement1", "Es gibt T1 Räume ohne Platzierung im Grundriss", {
          T1: cnt,
        });
        s += "\n";

        let firstRoom = true;
        for (const room of floor.rooms.values) {
          if (!room.roomtoFloorDrawingTransform && !room.isFloorGeneralRoom) {
            if (!firstRoom) {
              s += ", ";
            }
            firstRoom = false;
            s += room.tag;
          }
        }
        s += "\n";
        s += itx("ActivePanes.RoomsWithoutPlacement2", "Bitte platzieren Sie die Räume mit Drag & Drop");

        showToast({ text: s, time: 10000 });
      }
    }
  }

  activatePane = (id: string, subPageId?: string): WorkingPane | undefined => {
    for (let i = 0; i < this.items.length; i++) {
      const item = this.items[i];

      if (item && item.id === id && item.subPageId == subPageId) {
        this.setActiveId(id, subPageId);
        this.raiseOnChange("index");
        let selected: BaseModel | undefined;
        if (item.item?.className == "Page") {
          selected = (this.items[i].item as Page).getPageSelectionElement();
        }
        if (!selected) {
          if (item.item?.isBaseModel) {
            state.selectionManager.selectedObject = item.item as BaseModel;
          }
        } else {
          state.selectionManager.selectedObject = selected;
        }

        this.checkFloorplan(item);
        return item;
      }
    }

    return undefined;
  };

  closePane(id: string, subPageId?: string): boolean {
    for (let i = 0; i < this.items.length; i++)
      if (this.items[i].id === id && this.items[i].subPageId == subPageId) {
        this.items.splice(i, 1);
        const h = this.items.length - 1;
        if (h >= 0) {
          this.setActiveId(this.items[h].id);
        }

        this.raiseOnChange("close");
        return true;
      }
    return false;
  }
  closeAllPanes() {
    this.items = this.items.filter((pane) => !pane.closeable);
    if (this.items.length > 0) {
      this.setActiveId(this.items[0].id);
    } else {
      this.activeItemIndex.value = -1;
    }
  }

  openOrActivatePane(paneId: string) {
    if (this.isOpen(paneId)) {
      this.activatePane(paneId);
    } else {
      this.loadPage(paneId);
    }
  }

  isPdfOpen() {
    if (this.activeItemIndex.value >= 0) {
      const item = this.items[this.activeItemIndex.value]?.item;
      if (item) {
        return item.className == "PdfPageReference";
      }
    }
    return false;
  }

  isDrawingOpen() {
    if (this.activeItemIndex.value >= 0) {
      return this.items[this.activeItemIndex.value]?.item?.className == "Page";
    }
    return false;
  }

  isRoomOrFloorPlanOpen(): boolean {
    if (this.activeItemIndex.value >= 0) {
      return this.isDrawingOpen() && (this.items[this.activeItemIndex.value]?.item as Page).isRoomOrFloorPlan;
    }
    return false;
  }

  openRoomOrFloorPlan(): Page | undefined {
    if (this.activeItemIndex.value >= 0) {
      if (this.isDrawingOpen()) {
        const page = this.items[this.activeItemIndex.value]?.item as Page;
        if (page.isRoomOrFloorPlan) {
          return page;
        }
      }
    }
    return undefined;
  }

  isListOpen() {
    if (this.activeItemIndex.value >= 0) {
      return (
        this.isDrawingOpen() &&
        this.items[this.activeItemIndex.value]?.item &&
        pageTypeInfo((this.items[this.activeItemIndex.value]?.item as Page).documentType.type).isList
      );
    }
    return false;
  }

  documentTypeIsOpen(type: string) {
    if (this.activeItemIndex.value >= 0) {
      return (
        this.items[this.activeItemIndex.value]?.item &&
        this.isDrawingOpen() &&
        (this.items[this.activeItemIndex.value]?.item as Page).documentType.type == type
      );
    }
    return false;
  }

  pageIsOpen(): Page | undefined {
    if (this.activeItemIndex.value >= 0) {
      if (this.isDrawingOpen()) {
        return this.items[this.activeItemIndex.value]?.item && (this.items[this.activeItemIndex.value]?.item as Page);
      }
    }
    return undefined;
  }

  clear() {
    this.items = new Array<WorkingPane>();

    this.initFixedPanes();
    this.activeItemIndex.value = 0;

    this.raiseOnChange("clear");
  }
}
